import { buildFetchConfig } from '@/utils/fetchUtils';
import { createSSEProcessor } from '@/utils/sseProcessor';

/**
 * `promptfoo/utils/streamExtractors.js` (`extractJsonObjects`) intentionally
 * duplicates this brace-matching algorithm — see the comment on
 * `createSSEProcessor` in `src/utils/sseProcessor.ts` for why promptfoo can't
 * just import this file.
 */
const createJSONExtractor = (
  buffer: string,
  onChunk: (line: string) => void,
): {
  extract: () => string;
} => {
  let localBuffer = buffer;

  const extractJSON = (): string | null => {
    let depth = 0;
    let inString = false;
    let escape = false;
    let jsonEnd = -1;
    for (let i = 0; i < localBuffer.length; i++) {
      const char = localBuffer[i];
      if (escape) {
        escape = false;
        continue;
      }
      if (char === '\\') {
        escape = true;
        continue;
      }
      if (char === '"') {
        inString = !inString;
        continue;
      }
      if (!inString) {
        if (char === '{') depth++;
        else if (char === '}') {
          depth--;
          if (depth === 0) {
            jsonEnd = i + 1;
            break;
          }
        }
      }
    }
    if (jsonEnd > 0) {
      const jsonStr = localBuffer.substring(0, jsonEnd);
      localBuffer = localBuffer.substring(jsonEnd);
      return jsonStr;
    }
    return null;
  };

  const extract = (): string => {
    let jsonStr = extractJSON();
    while (jsonStr) {
      onChunk(jsonStr);
      jsonStr = extractJSON();
    }
    return localBuffer;
  };

  return { extract };
};

/**
 * Shared read loop for a streamed fetch `Response`: drains the reader, decodes
 * chunks, and routes them to the SSE or bare-JSON extractor based on
 * content-type. Used by both `streamingPost` and `streamingPostFormData`,
 * which differ only in how they build the request.
 */
const consumeStream = async (
  response: Response,
  onChunk: (line: string) => void,
): Promise<void> => {
  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  if (!reader) throw new Error('Response body is not readable');

  const contentType = response.headers.get('content-type') || '';
  const isSSE = contentType.includes('text/event-stream');
  let buffer = '';
  let streaming = true;

  while (streaming) {
    const { done, value } = await reader.read();
    if (done) {
      if (isSSE) {
        const processor = createSSEProcessor(buffer, onChunk);
        buffer = processor.process(true);
      } else {
        const extractor = createJSONExtractor(buffer, onChunk);
        buffer = extractor.extract();
      }
      streaming = false;
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    if (isSSE) {
      const processor = createSSEProcessor(buffer, onChunk);
      buffer = processor.process(false);
    } else {
      const extractor = createJSONExtractor(buffer, onChunk);
      buffer = extractor.extract();
    }
  }
};

export const streamingPost = async (
  url: string,
  data: unknown,
  onChunk: (line: string) => void,
  additionalHeaders?: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> => {
  const { baseURL, headers } = buildFetchConfig(additionalHeaders);

  const response = await fetch(`${baseURL}${url}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
    signal,
  });

  if (!response.ok) {
    console.error('[lib_get_service] Request failed!');
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  await consumeStream(response, onChunk);
};

export const streamingPostFormData = async (
  url: string,
  formData: FormData,
  onChunk: (line: string) => void,
  additionalHeaders?: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> => {
  const { baseURL, headers } = buildFetchConfig(additionalHeaders);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { 'Content-Type': _, ...headersWithoutContentType } = headers;

  const response = await fetch(`${baseURL}${url}`, {
    method: 'POST',
    headers: headersWithoutContentType,
    body: formData,
    signal,
  });

  if (!response.ok) {
    console.error('[lib_get_service] FormData upload failed!');
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  await consumeStream(response, onChunk);
};
